import { useCallback, useMemo, useState } from 'react';
import { useCreateBoat } from '@/modules/inventory/hooks/useBoats';
import { useStatuses } from '@/modules/inventory/hooks/useStatuses';
import type { BoatFormData, InventoryMediaItem } from '@/modules/inventory/types/boat.types';

export interface CreateDraftResult {
  id: string;
  images?: InventoryMediaItem[];
  primary_image?: InventoryMediaItem | null;
}

export interface DraftBasicInfo {
  condition: string;
  manufacturer: string;
  model: string;
  model_year: number;
}

export interface DraftSyncReturn {
  boatId: string | null;
  isCreating: boolean;
  isSaving: boolean;
  lastSavedAt: Date | null;
  saveError: string | null;
  hasServerDraft: boolean;
  createDraft: (basicInfo: DraftBasicInfo) => Promise<CreateDraftResult>;
  resetDraft: () => void;
  setBoatId: (id: string) => void;
}

/**
 * Manages the server-side draft lifecycle:
 * - createDraft: POST /boats with status=draft (Step 1)
 * Updates go through the changes-only save (useCanvasAutoSave, #1177).
 */
export function useWizardDraftSync(): DraftSyncReturn {
  const [boatId, setBoatId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const { mutateAsync: createBoat } = useCreateBoat();

  // #85 follow-up: every wizard draft write carries status_id pointing at
  // the Draft system row. The id is per-tenant; resolve from the statuses
  // catalog (cached by TanStack Query, so no extra round-trip in practice).
  const { data: statusesResponse } = useStatuses();
  const draftStatusId = useMemo(() => {
    const items = statusesResponse?.items ?? [];
    return items.find((s) => s.is_system === true && s.value === 'draft')?.id ?? null;
  }, [statusesResponse]);

  const hasServerDraft = boatId !== null;

  /**
   * Create a draft boat on the server.
   * Called after Step 1 validation passes.
   * Returns the new boat ID on success, throws on failure.
   */
  const createDraft = useCallback(
    async (basicInfo: DraftBasicInfo): Promise<CreateDraftResult> => {
      setIsCreating(true);
      setSaveError(null);

      if (!draftStatusId) {
        throw new Error('Draft status not yet loaded — try again in a moment');
      }

      try {
        const payload = {
          condition: basicInfo.condition,
          manufacturer: basicInfo.manufacturer,
          model: basicInfo.model,
          model_year: basicInfo.model_year,
          // #85 follow-up: status_id=Draft is the only marker the backend
          // needs. Validation relaxes required fields when status_id=Draft,
          // and the saving observer derives website_status='disabled'.
          status_id: draftStatusId,
          our_price: 0,
        } as BoatFormData;

        const result = (await createBoat(payload)) as {
          id?: string | number;
          images?: InventoryMediaItem[];
          primary_image?: InventoryMediaItem | null;
        };

        const id = String(result.id ?? '');
        if (!id) {
          throw new Error('No boat ID returned from server');
        }

        setBoatId(id);
        setLastSavedAt(new Date());

        // Return the full shape so callers can re-hydrate their local media
        // state with the server's authoritative inventory_images ids.
        return {
          id,
          images: result.images,
          primary_image: result.primary_image ?? null,
        };
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Failed to create draft';
        setSaveError(message);
        throw error;
      } finally {
        setIsCreating(false);
      }
    },
    [createBoat, draftStatusId],
  );

  /**
   * Reset all draft state. Called when wizard closes or user starts fresh.
   */
  const resetDraft = useCallback(() => {
    setBoatId(null);
    setIsCreating(false);
    setIsSaving(false);
    setLastSavedAt(null);
    setSaveError(null);
  }, []);

  return {
    boatId,
    isCreating,
    isSaving,
    lastSavedAt,
    saveError,
    hasServerDraft,
    createDraft,
    resetDraft,
    setBoatId,
  };
}
